import { useEffect, useState } from "react";
import { getBrandingData } from "../../api/branding/branding.js";
import { JWTProvider, useJWTContext } from "../../context/JWTProvider";
import { isChatsInactiveMoreThan30Minutes } from "../../lib/helper/chatInactivityManager";
import { clearChatLocalStorage } from "../../lib/helper/clearChatLocalStorage";
import Cancel from "../Icons/Cancel";
import "./chat.css";
import { ChatContainer } from "./ChatContainer.jsx";

// Inner component that uses JWT context
const ChatContent = ({
  apiKey,
  chatImage,
  widgetKey,
  notificationMessage,
  chatBotFeatures,
}) => {
  const [isChatWindowOpen, setIsChatWindowOpen] = useState(
    chatBotFeatures.isChatBotOpen ?? false,
  );
  const [isChatNotificationVisible, setIsChatNotificationVisible] =
    useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const [isMaximizeChatSize, setIsMaximizeChatSize] = useState(true);
  const [branding, setBranding] = useState(null);
  const [isBrandLoading, setIsBrandLoading] = useState(false);

  const { isTokenInitialized, tokenError } = useJWTContext();

  useEffect(() => {
    if (isChatsInactiveMoreThan30Minutes()) {
      clearChatLocalStorage();
    }
  }, []);

  useEffect(() => {
    if (tokenError) {
      console.error("Widget: Authentication failed; widget hidden.", tokenError);
    }
  }, [tokenError]);

  // Branding is public; loading it must not initialize a token.
  useEffect(() => {
    if (!widgetKey) return;
    const controller = new AbortController();
    setBranding(null);
    setIsBrandLoading(true);
    getBrandingData(widgetKey, chatBotFeatures.shouldSkipCache, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setBranding(data);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error("Widget: Unable to load branding; widget hidden.", error.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsBrandLoading(false);
      });
    return () => controller.abort();
  }, [widgetKey, chatBotFeatures.shouldSkipCache]);

  // Auto-open chat if existing session found
  useEffect(() => {
    if (localStorage.getItem("cid") && isTokenInitialized) {
      handleIsChatWindowOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenInitialized]);

  const renderButtonLabel = () => {
    if (chatImage.src !== "" && !isChatWindowOpen) {
      return (
        <img
          src={chatImage.src}
          className={`curai-rounded-md`}
          style={{
            width: `${chatImage.width}px`,
            height: `${chatImage.height}px`,
          }}
          alt="Chatbot"
        />
      );
    } else {
      return (
        <img
          src={`${process.env.REACT_APP_EMBED_URL}/assets/chat-icon-light.svg`}
          className={`curai-rounded-md`}
          style={{
            width: `${chatImage.width}px`,
            height: `${chatImage.height}px`,
          }}
          alt="Chatbot"
        />
      );
    }
  };

  const handleChatToggle = () => {
    setIsChatWindowOpen((prev) => !prev);
  };

  const handleIsChatWindowOpen = (chatWindowOpen) => {
    setIsChatWindowOpen(chatWindowOpen);
  };

  useEffect(() => {
    if (isChatWindowOpen) {
      setIsChatNotificationVisible(false);
      clearTimeout(timeoutId);
    }
  }, [isChatWindowOpen, timeoutId]);

  useEffect(() => {
    if (
      notificationMessage?.title &&
      notificationMessage?.body &&
      notificationMessage?.delay &&
      !isChatWindowOpen
    ) {
      const timeoutId = setTimeout(() => {
        setIsChatNotificationVisible(true);
      }, parseInt(notificationMessage?.delay));
      setTimeoutId(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationMessage?.delay, isChatWindowOpen]);

  if (!branding || isBrandLoading || branding?.ule?.is_cue || tokenError) {
    return null;
  }

  return (
    <>
      <div
        className={`curai-fixed curai-z-50 curai-flex curai-justify-center  chat-bot-wrapper-curai ${
          !isChatWindowOpen
            ? "curai-flex curai-flex-col curai-items-end curai-bottom-5 curai-right-5 curai-h-auto curai-w-auto curai-gap-2"
            : !isMaximizeChatSize
              ? "curai-top-1/2 curai-flex-col curai-items-center curai-left-1/2 -curai-translate-x-1/2 -curai-translate-y-1/2 chat-fonts curai-max-h-[700px] curai-h-[90dvh] curai-w-[80dvw] curai-max-w-[1000px]"
              : "curai-bottom-5 curai-right-5 curai-flex curai-justify-center curai-flex-col curai-items-center chat-fonts curai-max-h-[580px] curai-h-[80dvh] curai-w-[90dvw] curai-max-w-[400px]"
        }`}
      >
        {isChatWindowOpen && (
          <ChatContainer
            widgetKey={widgetKey}
            apiKey={apiKey}
            isChatWindowOpen={isChatWindowOpen}
            branding={branding}
            isBrandLoading={isBrandLoading}
            handleIsChatWindowOpen={handleIsChatWindowOpen}
            isMaximizeChatSize={isMaximizeChatSize}
            setIsMaximizeChatSize={setIsMaximizeChatSize}
            handleChatToggle={handleChatToggle}
            chatBotFeatures={chatBotFeatures}
          />
        )}

        {isChatNotificationVisible && (
          <div className="curai-bg-[#fefafa] curai-rounded-md curai-p-5 curai-relative curai-bubble-shadow curai-max-w-[290px] curai-border curai-border-solid curai-border-slate-400 curai-text-left">
            <button
              className="curai-bg-[grey] curai-border-none curai-absolute curai-right-3 -curai-top-3 curai-rounded-full curai-h-7 curai-w-7 curai-flex curai-justify-center curai-items-center curai-bubble-close-button curai-text-white"
              aria-hidden="true"
              onClick={() => setIsChatNotificationVisible(false)}
            >
              <Cancel styleClass="curai-w-4 curai-h-4" />
            </button>
            <p
              className="curai-mt-0 curai-text-[#4d5056] curai-text-base curai-mb-3 curai-font-bold"
              aria-live="polite"
              id="curatu-notification-title"
            >
              {notificationMessage?.title}
            </p>
            <p className="curai-mb-0 curai-text-[#020c27] curai-text-sm">
              {notificationMessage?.body}
            </p>
            <button
              className="curai-sr-only curai-skip-link"
              onClick={() => setIsChatNotificationVisible(false)}
            >
              Close
              <span className="curai-sr-only"> notification section</span>
            </button>
          </div>
        )}

        <button
          className={`curai-w-[100px] curai-h-[100px] curai-bg-none curai-rounded-full curai-border-none curai-text-white curai-bg-transparent curai-p-0 curai-items-end ${
            isChatWindowOpen ? "curai-hidden" : ""
          }`}
          onClick={handleChatToggle}
          id="curatu-start-chat-session"
        >
          {renderButtonLabel()}
        </button>
      </div>
    </>
  );
};

// Main Chat component with JWTProvider wrapper
const Chat = (props) => {
  const [enableJWTInit, setEnableJWTInit] = useState(false);

  // Check if user has existing chat session in localStorage
  useEffect(() => {
    if (localStorage.getItem("cid")) {
      setEnableJWTInit(true);
    }
  }, []);

  // Enable JWT initialization on first user interaction
  const handleFirstInteraction = () => {
    setEnableJWTInit(true);
  };

  return (
    <JWTProvider enableInitialization={enableJWTInit}>
      <div onClick={handleFirstInteraction} onKeyDown={handleFirstInteraction}>
        <ChatContent {...props} />
      </div>
    </JWTProvider>
  );
};

export default Chat;
